import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import {
  fetchSubCategories,
  fetchSubCategoryById,
  deleteSubCategory,
  updateSubCategoryStatus,
  clearError,
  clearSuccess,
} from "../../redux/slices/subCategorySlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SubCategoryModal from "./SubCategoryModal";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const SubCategoryDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);

  const {
    currentSubCategory,
    isLoading,
    error,
    success,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.subCategories);

  const { categories } = useSelector((state) => state.categories);

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategories());
    if (id) {
      dispatch(fetchSubCategoryById(id));
    }
    // Clear any existing messages when component mounts
    dispatch(clearError());
    dispatch(clearSuccess());
  }, [dispatch, id]);

  // Clear messages and show toasts (only once per message)
  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      // Handle both string and object error messages
      const errorMessage =
        typeof error === "string"
          ? error
          : error.message || "An error occurred";
      toast.error(errorMessage);
      lastErrorRef.current = error;
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success && success !== lastSuccessRef.current) {
      toast.success(success);
      lastSuccessRef.current = success;
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
    };
  }, [dispatch]);

  const handleEdit = () => {
    navigate(`/sub-category/edit/${id}`);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      dispatch(deleteSubCategory(id));
      setTimeout(() => {
        navigate("/sub-category");
      }, 1000);
    }
  };

  const handleStatusToggle = () => {
    const newStatus =
      currentSubCategory?.status === "Active" ? "Inactive" : "Active";
    dispatch(updateSubCategoryStatus({ id, status: newStatus }));
  };

  const handleBack = () => {
    navigate("/sub-category");
  };

  const handleViewParentCategory = () => {
    navigate(`/category/details/${currentSubCategory?.category_id}`);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  if (isLoading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!currentSubCategory) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>SubCategory not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to SubCategories
          </Button>
        </div>
      </Container>
    );
  }

  const handleEditSubCategory = (subCategoryId) => {
    setEditingSubCategory(subCategoryId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSubCategory(null);
    // Refresh subcategories list after modal closes
    dispatch(fetchSubCategories());
  };

  return (
    <Container className="view-form">
      <SubCategoryModal
        show={showModal}
        handleClose={handleCloseModal}
        editId={editingSubCategory}
      />

      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Subcategory Details</h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short  fs-3"></i>
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handleEditSubCategory(currentSubCategory?.id)}
              >
                <FaPencilAlt />
              </Button>
              {/* <Button 
                variant="outline-light" 
                size="sm" 
                onClick={handleStatusToggle}
                disabled={isUpdating}
              >
                {currentSubCategory?.status === "Active" ? "Deactivate" : "Activate"}
              </Button> */}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <FaRegTrashAlt />
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              {/* SubCategory Information Section */}
              <Row className="my-2 view-form">
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Subcategory Name</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentSubCategory?.name || "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Status</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Badge
                      bg={
                        currentSubCategory?.status === "Active"
                          ? "success"
                          : "secondary"
                      }
                    >
                      {currentSubCategory?.status || "-"}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Parent Category</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Badge bg="info">
                      {getCategoryName(currentSubCategory?.category_id)}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Created Date</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentSubCategory?.created_at
                      ? formatDateToDDMMYYYY(currentSubCategory.created_at)
                      : "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Last Updated</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentSubCategory?.updated_at
                      ? formatDateToDDMMYYYY(currentSubCategory.updated_at)
                      : "-"}
                  </p>
                </Col>
              </Row>

              {/* Description Section */}
              <Row className="mx-3 my-2 view-form">
                <Col lg={12} className="my-2">
                  <label className="fw-bold">Description</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                      minHeight: "40px",
                    }}
                  >
                    {currentSubCategory?.description ||
                      "No description provided"}
                  </p>
                </Col>
              </Row>

              {/* SubCategory Image Section */}
              <Row className="mx-3 my-2 view-form">
                <Col lg={12} className="my-2">
                  <label className="fw-bold">Subcategory Image</label>
                  <div
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "15px",
                    }}
                  >
                    {currentSubCategory?.image_url ? (
                      <div className="mt-2">
                        <img
                          src={currentSubCategory.image_url}
                          alt={currentSubCategory.name}
                          style={{
                            maxWidth: "300px",
                            maxHeight: "200px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "2px solid #dee2e6",
                          }}
                          onError={(e) => {
                            console.log(
                              "Image load error:",
                              currentSubCategory.image_url
                            );
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "block";
                          }}
                        />
                        <div
                          className="text-muted mt-2"
                          style={{ display: "none" }}
                        >
                          <i className="fas fa-image fa-2x mb-2"></i>
                          <p>Failed to load image</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted mt-2">
                        <i className="fas fa-image fa-2x mb-2"></i>
                        <p>No image available</p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SubCategoryDetails;
